import { ROLES, type Role } from '@account-manager/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../auth/Can.js';
import { useAbility } from '../../auth/useAbility.js';

interface Member {
  user_id: string;
  email: string;
  name: string | null;
  role: Role;
}

export function Members() {
  const { t } = useTranslation('settings');
  const { activeOrgId } = useAuth();
  const ability = useAbility();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['members', activeOrgId],
    queryFn: () => api<{ members: Member[] }>(`/orgs/${activeOrgId}/members`),
    enabled: !!activeOrgId,
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api(`/orgs/${activeOrgId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });

  const remove = useMutation({
    mutationFn: (userId: string) =>
      api(`/orgs/${activeOrgId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold">{t('members')}</h1>
      <table className="w-full bg-white">
        <thead className="border-b text-left text-sm text-slate-600">
          <tr>
            <th className="p-2">Email</th>
            <th className="p-2">Nombre</th>
            <th className="p-2">{t('role')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data?.members.map((m) => (
            <tr key={m.user_id} className="border-b text-sm">
              <td className="p-2">{m.email}</td>
              <td className="p-2">{m.name ?? '—'}</td>
              <td className="p-2">
                {ability.can('manage', 'Member') ? (
                  <select
                    value={m.role}
                    onChange={(e) =>
                      changeRole.mutate({ userId: m.user_id, role: e.target.value as Role })
                    }
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>{m.role}</span>
                )}
              </td>
              <td className="p-2">
                <Can I="manage" a="Member">
                  <button
                    type="button"
                    onClick={() => remove.mutate(m.user_id)}
                    className="text-xs text-red-600"
                  >
                    {t('removeMember')}
                  </button>
                </Can>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
